import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  uniqBy,
  filter,
  merge,
} from 'lodash';
import {
  withMobileDialog,
  withStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
} from '@material-ui/core';

import { resetNewEvents } from '@pingum/app-state-management/event/actions';

// associated components
import EventActions from '../../EventActions';

// widgets
import EventStats from '../../../../widgets/EventStats';
import EventStatus from '../../../../widgets/EventStatus';
import EventStateLabel from '../../../../widgets/EventStateLabel';
import User from '../../../../widgets/User';

// utils
import {
  getColorForItem,
  getIconForItem,
} from '../../../../../utils/iconHelper';
import { formatDateString } from '../../../../../utils/dateHelper';

import styles from './styles';
import UserList from '../../../../widgets/UserList';

export const buildEventTableRow = (
  event,
  statuses,
  isNewEvent,
  handleRowClick,
  handleChangeStatusClick,
  handleResetNewEvents,
  classes,
  restricted,
  fullScreen,
) => {
  const {
    currentEventStatus = {
      status: {
        id: -1,
        name: 'No status',
      },
      createdDate: null,
    },
  } = event;

  const statusColor = getColorForItem(statuses, currentEventStatus.status.id);
  const statusIcon = getIconForItem(statuses, currentEventStatus.status.id);

  const contributors = filter(uniqBy(merge(
    [{
      email: event.createdByEmail,
      userFullName: event.createdByName,
    }],
    [{
      email: event.updatedByEmail,
      userFullName: event.updatedByName,
    }],
    (event.comments || []).map(({
      createdByEmail,
      createdByName,
    }) => ({
      email: createdByEmail,
      userFullName: createdByName,
    })),
    (event.eventStatus || []).map(({
      createdByEmail,
      createdByName,
    }) => ({
      email: createdByEmail,
      userFullName: createdByName,
    })),
  ), 'email'), (user) => user.email);

  return (
    <TableRow
      className={[
        classes.row,
        event.new
          ? classes.new
          : '',
      ].join(' ')}
      key={`event-${event.id}`}
      onFocus={() => { isNewEvent && handleResetNewEvents(); }}
      onMouseOver={() => { isNewEvent && handleResetNewEvents(); }}
      style={{ borderLeft: `4px solid ${statusColor}` }}
      onClick={() => handleRowClick(event.id)}
    >
      <TableCell className={classes.eventNameTableCell}>
        <div className={classes.eventNameContainer}>
          <EventStateLabel archived={!!event.archived} completed={!!event.completeDate} dueDate={event.dueDate} />
          <span className={classes.eventName}>{event.name}</span>
        </div>
        <Typography variant="caption" component="div" className={classes.eventIdField}>#{event.id}</Typography>
      </TableCell>
      <TableCell className={classes.statusCell}>
        <EventStatus
          eventStatus={currentEventStatus}
          color={statusColor}
          icon={statusIcon}
          hideComment
          hideDate
          archived={!!event.archived}
          completed={!!event.completeDate}
          showDuration={!event.completeDate}
        />
      </TableCell>
      <TableCell>
        {
          currentEventStatus.assignment
            ? (currentEventStatus.assignment.split(/(?=@\[)/)
              .map((user) => (
                <User
                  key={`event-${event.id}-user-${user.match(/@\[.*\]\((.*)\)/)[1]}`}
                  chip
                  user={{ auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1] }}
                  mini={fullScreen}
                />
              )))
            : (
              '-'
            )
        }
      </TableCell>
      <TableCell>
        {formatDateString(event.startDate)}
      </TableCell>
      <TableCell>
        {
          event.dueDate
            ? formatDateString(event.dueDate)
            : '-'
        }
      </TableCell>
      <TableCell>
        <UserList
          users={contributors}
          mini
          // chip
          // bordered
          maxUsers={3}
        />
      </TableCell>
      <TableCell>
        <EventStats event={event} />
      </TableCell>
      {
        !restricted
          && (
            <TableCell className={classes.eventActions}>
              <EventActions
                event={event}
                openEventDetailsPage={() => handleRowClick(event.id, true)}
                openHistoryDialog={() => handleChangeStatusClick(event)}
              />
            </TableCell>
          )
      }
    </TableRow>
  );
};

export class ListViewComponent extends React.Component {
  handleRowClick = (id, openInEditMode) => {
    this.props.handleSelectEvent(id, openInEditMode || false);
  }

  handleChangeStatusClick = (event) => {
    this.props.handleChangeStatusClick(event);
  }

  render() {
    const {
      classes,
      isAddingEvent,
      isUpdatingEvent,
      isChangingStatus,
      newEvents,
      events,
      restricted,
      fullScreen,
    } = this.props;

    const combinedEvents = [
      ...newEvents.map((event) => ({
        ...event,
        new: true,
      })),
      ...events,
    ];

    return (
      <Paper classes={{ root: classes.paperRoot }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell varient="head">Name</TableCell>
              <TableCell varient="head">Status</TableCell>
              <TableCell varient="head">Assignment</TableCell>
              <TableCell varient="head">Created</TableCell>
              <TableCell varient="head">Due Date</TableCell>
              <TableCell varient="head">Contributors</TableCell>
              <TableCell varient="head" />
              {
                !restricted
                  && <TableCell varient="head" />
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              combinedEvents.map((event) => buildEventTableRow(
                event,
                this.props.statuses,
                false,
                this.handleRowClick,
                this.handleChangeStatusClick,
                this.props.resetNewEvents,
                classes,
                restricted,
                fullScreen,
              ))
            }
          </TableBody>
        </Table>
        {
          /* Putting this here because we need some prop update in this component
          on event add or update so this component rerenders with the new data. Without
          this the list won't add new events or update existing ones. */
          (
            isAddingEvent
              || isUpdatingEvent
              || isChangingStatus
          )
            && (
              <Tooltip title="Completing Action">
                <CircularProgress className={classes.isUpdatingProgress} />
              </Tooltip>
            )
        }
      </Paper>
    );
  }
}

ListViewComponent.propTypes = {
  classes: PropTypes.object,
  events: PropTypes.array,
  handleSelectEvent: PropTypes.func,
  newEvents: PropTypes.array,
  statuses: PropTypes.array,
  isAddingEvent: PropTypes.bool,
  isUpdatingEvent: PropTypes.bool,
  isChangingStatus: PropTypes.bool,
  resetNewEvents: PropTypes.func,
  handleChangeStatusClick: PropTypes.func,
  restricted: PropTypes.bool,
  fullScreen: PropTypes.bool,
};

ListViewComponent.defaultProps = {
  classes: {},
  events: [],
  handleSelectEvent: () => {},
  statuses: [],
  newEvents: [],
  isAddingEvent: false,
  isUpdatingEvent: false,
  isChangingStatus: false,
  resetNewEvents: () => {},
  handleChangeStatusClick: () => {},
  restricted: false,
  fullScreen: false,
};

export const mapStateToProps = (state) => ({
  events: state.event.eventsPageable.content,
  statuses: state.status.statuses,
  newEvents: state.event.newEvents,
  isAddingEvent: state.event.isAddingEvent,
  isUpdatingEvent: state.event.isUpdatingEvent,
  isChangingStatus: state.event.isChangingStatus,
});

export const mapDispatchToProps = (dispatch) => ({ resetNewEvents: () => dispatch(resetNewEvents()) });

export const ListViewConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'md' })(withStyles(styles, { withTheme: true })(ListViewComponent)));

export default withRouter(ListViewConnected);
